import React, { useCallback, useEffect, useState } from 'react';
import userflow from 'userflow.js';
import ListItemBlock from '../../common/list-item-block/ListItemBlock';
import { ROUTES } from '../../common/constants/routes';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '../../../static/assets/svg/add-icon.svg';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TaskItem from '../../common/task-item-block/task-item-block';
import ListItemButton from '../../common/list-item-button/ListItemButton';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { deleteTodo, fetchTodos, updateTodo } from '../../slices/todo/todoSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import TodoForm from './components/TodoForm/TodoForm';
import { ToDoStatus } from '../../common/task-item-block/types';
import { ToDo } from '../../models/todo.model';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CompanyCreateForm from '../../common/company-create-form';
import { fetchCompanies, selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchEmployees, selectAllEmployees } from '../../slices/employees/employeesSlice';
import { fetchSuppliers, selectAllSuppliers } from '../../slices/suppliers/suppliersSlice';
import { fetchCustomers, selectAllCustomers } from '../../slices/customers/customersSlice';
import { fetchDocuments, selectAllDocuments } from '../../slices/documents/documentsSlice';
import {
  addDocumentContract,
  fetchDocumentContracts,
  selectAllDocumentContracts,
} from '../../slices/document-contracts/documentContractsSlice';
import { selectUser } from '../../slices/user/userSlice';
import { RootState } from '../../store';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import { AIDocumentContractModel } from '../../models/ai.document.contract.model';
import { NWClient } from '../../client/NWClient';
import { LibraryItem } from '../library-v2/components/library-list/types';
import { showMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { Helpers } from '../../common/helpers/helpers';
import { toast } from 'react-toastify';
import { useClerk } from '@clerk/clerk-react';
import BookACallModal from '../../common/book-a-call-modal/BookACallModal';

const HomePageBlock: React.FC = () => {
  const currentUser = useAppSelector(selectUser);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const todosPending = useAppSelector((state: RootState) => state.todos.status);
  const fetchingCompanyError = useAppSelector((state: RootState) => state.companies.error);
  const { signOut } = useClerk();

  const documentsMenuItems = [
    // {
    //   name: t('menuItems.createNewBlankDocument'),
    //   onClick: () => {
    //     setIsBlankModalOpen(true);
    //   },
    // },
    {
      name: t('menuItems.createNewBlankDocument'),
      onClick: () => {
        setIsBlankModalOpen(true);
      },
    },
    {
      name: t('menuItems.createFromBusinessKitzLibrary'),
      onClick: () => {
        navigate(ROUTES.DOCUMENT_LIBRARY + '?businessKitzLibrary=true');
      },
    },
    {
      name: t('menuItems.createFromOwnLibrary'),
      onClick: () => {
        navigate(ROUTES.DOCUMENT_LIBRARY + '?ownLibrary=true');
      },
    },
  ];
  const profilesMenuItems = [
    {
      name: t('menuItems.createEmployee'),
      onClick: () => {
        navigate(ROUTES.EMPLOYEE_PROFILES);
      },
    },
    {
      name: t('menuItems.createSupplier'),
      onClick: () => {
        navigate(ROUTES.SUPPLIER_PROFILES);
      },
    },
    {
      name: t('menuItems.createCustomer'),
      onClick: () => {
        navigate(ROUTES.CUSTOMER_PROFILES);
      },
    },
  ];

  const dispatch = useAppDispatch();
  const todos = useAppSelector((state) => state.todos.items);
  const company = useAppSelector(selectAllCompanies)[0];
  const companyLoadingStatus = useAppSelector((state) => state.companies.status);
  const freePlan = !company?.stripe_product_id && !company?.plan_settings.stripe_product_id;

  const employees = useAppSelector(selectAllEmployees);
  const customers = useAppSelector(selectAllCustomers);
  const suppliers = useAppSelector(selectAllSuppliers);
  const documentUploads = useAppSelector(selectAllDocuments);
  const documentContracts = useAppSelector(selectAllDocumentContracts);
  const [isSkipped, setIsSkipped] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [documentUploadsLoading, setDocumentUploadsLoading] = useState<boolean>(true);
  const [documentContractsLoading, setDocumentContractsLoading] = useState<boolean>(true);
  const [suppliersLoading, setSuppliersLoading] = useState<boolean>(true);
  const [customersLoading, setCustomersLoading] = useState<boolean>(true);
  const [employeesLoading, setEmployeesLoading] = useState<boolean>(true);
  const [pendingDownload, setPendingDownload] = useState<boolean>(false);
  const [todosLoading, setTodosLoading] = useState<boolean>(true);
  const [open, setOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [createNewDocument, setCreateNewDocument] = useState<string>('selectAnOption');
  const [isBlankModalOpen, setIsBlankModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templates, setTemplates] = useState<LibraryItem[]>([]);

  const recentDocumentsArraySorted = [...documentUploads, ...documentContracts]
    .slice()
    .sort(function (a, b) {
      return Number(new Date(b.updated)) - Number(new Date(a.updated));
    })
    .slice(0, 4);

  const toggleShowBookingModal = () => {
    setIsBookingModalOpen((prev) => !prev);
  };

  const navigateToTemplatePage = (category: string, id: number) => {
    navigate(`${ROUTES.DOCUMENT_LIBRARY}/${category}/${id}?templateViewEditMode=true`);
  };

  const handleS3DownloadLink = (id: number, filename: string) => {
    setPendingDownload(true);
    NWClient.downloadLibraryDocument(id, filename)
      .catch((error) => {
        const errorMessage = Helpers.returnErrorText(error);
        toast.error(errorMessage.toString());
      })
      .finally(() => {
        setPendingDownload(false);
      });
  };

  const handleNavigateTemplatePage = (item: LibraryItem) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    if (item.download_link) {
      handleS3DownloadLink(item.id, item.name);
    } else {
      if (freePlan) {
        if (actionAvailableFree) {
          navigateToTemplatePage('region' in item ? item.region : 'company-templates', item.id);
        } else {
          dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
        }
      } else {
        navigateToTemplatePage('region' in item ? item.region : 'company-templates', item.id);
      }
    }
  };

  const templatesArray = [...templates].map((el) => {
    return {
      id: el.id,
      name: el.name,
      isFreeLabel: el.is_free,
      onListItemClick: () => handleNavigateTemplatePage(el),
    };
  });

  const recentDocuments = recentDocumentsArraySorted.map((el) => ({
    id: el.id,
    name: el.name,
    labelText: el.category,
    onListItemClick:
      'contract_generation' in el
        ? () => navigate(`${ROUTES.DOCUMENT_VAULT}/${el.id}?initialTab=0&contractGenerated=true`)
        : () => navigate(`${ROUTES.DOCUMENT_VAULT}/${el.id}`),
  }));

  const recentProfilesArraySorted = [...employees, ...customers, ...suppliers]
    .slice()
    .sort(function (a, b) {
      return Number(new Date(b.updated)) - Number(new Date(a.updated));
    })
    .slice(0, 4);

  const getProfileUrl = (el: any) => {
    if ('supplier_type' in el) {
      return ROUTES.SUPPLIER_PROFILES;
    }
    if ('customer_name' in el) {
      return ROUTES.CUSTOMER_PROFILES;
    }
    if ('employment_type' in el) {
      return ROUTES.EMPLOYEE_PROFILES;
    }
  };

  const getProfileCategory = (el: any) => {
    if ('supplier_type' in el) {
      return t('labels.supplier');
    }
    if ('customer_name' in el) {
      return t('labels.customer');
    }
    if ('employment_type' in el) {
      return t('labels.employee');
    }
  };

  const getProfileName = (el: any) => {
    if ('supplier_type' in el) {
      return el.name;
    }
    if ('customer_name' in el) {
      return el.customer_name;
    }
    if ('employment_type' in el) {
      return el.full_name;
    }
  };

  const recentProfiles = recentProfilesArraySorted.map((el) => ({
    id: el.id,
    name: getProfileName(el),
    labelText: getProfileCategory(el),
    onListItemClick: () => navigate(`${getProfileUrl(el)}/${el.id}`),
  }));

  useEffect(() => {
    if (isSkipped && company) {
      dispatch(fetchTodos())
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  }, [isSkipped, company]);

  const handleSkip = () => {
    setIsSkipped(true);
  };
  const handleAction = (action: string, todo: any) => {
    if (action === 'delete') {
      setTodosLoading(true);
      dispatch(deleteTodo({ id: todo.id }))
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
    if (action === 'modify') {
      setSelectedTodo(todo);
      setOpen(true);
    }
  };
  const handleStart = (todoId: number) => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo) {
      setTodosLoading(true);
      dispatch(
        updateTodo({
          id: todoId,
          data: {
            ...todo,
            started_at: new Date().toISOString(),
          },
        })
      )
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  };

  const handleComplete = (todoId: number) => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo) {
      setTodosLoading(true);
      dispatch(
        updateTodo({
          id: todoId,
          data: {
            ...todo,
            completed_at: new Date().toISOString(),
          },
        })
      )
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  };

  const handleContinue = (todoId: number) => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo) {
      setTodosLoading(true);
      dispatch(
        updateTodo({
          id: todoId,
          data: {
            ...todo,
            completed_at: null,
            started_at: new Date().toISOString(),
          },
        })
      )
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  };

  const getTodoStatus = (todo: ToDo): ToDoStatus => {
    if (todo.completed_at) {
      return 'completed';
    }
    const now = new Date();
    const dueDate = new Date(todo.due_at);
    if (now > dueDate) {
      return 'overdue';
    }
    return 'in_progress';
  };
  const sortTodos = (todos: ToDo[]): ToDo[] => {
    const groups: { [key in ToDoStatus]: ToDo[] } = {
      overdue: [],
      in_progress: [],
      completed: [],
    };

    todos.forEach((todo) => {
      const status = getTodoStatus(todo);
      groups[status].push(todo);
    });
    groups.overdue.sort((a, b) => new Date(b.due_at).getTime() - new Date(a.due_at).getTime());
    groups.in_progress.sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime());
    return [...groups.overdue, ...groups.in_progress, ...groups.completed];
  };

  const handleOpen = () => {
    setSelectedTodo(null);
    setOpen(true);
  };
  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      return;
    }
    setOpen(false);
    setSelectedTodo(null);
  };
  useEffect(() => {
    switch (createNewDocument) {
      case 'createFromBusinessKitzLibrary':
        navigate(ROUTES.DOCUMENT_LIBRARY + '?businessKitzLibrary=true');
        break;
      case 'createFromOwnLibrary':
        navigate(ROUTES.DOCUMENT_LIBRARY + '?ownLibrary=true');
        break;
      case 'createNewBlankDocument':
        setIsBlankModalOpen(true);
        break;
    }
  }, [createNewDocument]);
  useEffect(() => {
    const closeHandler = () => setOpen(false);
    document.addEventListener('closeModal', closeHandler);
    return () => {
      document.removeEventListener('closeModal', closeHandler);
    };
  }, []);

  const getCompanyAndProfiles = useCallback(() => {
    if (!company) {
      dispatch(fetchCompanies());
    }
    if (company) {
      dispatch(fetchEmployees({ company_id: company.id }))
        .unwrap()
        .finally(() => setEmployeesLoading(false));
      dispatch(fetchSuppliers({ company_id: company.id }))
        .unwrap()
        .finally(() => setSuppliersLoading(false));
      dispatch(fetchCustomers({ company_id: company.id }))
        .unwrap()
        .finally(() => setCustomersLoading(false));
    }
  }, [company, dispatch]);

  const getDocuments = useCallback(() => {
    if (currentUser?.id && company) {
      dispatch(fetchDocuments({ data: { user: currentUser.id } }))
        .unwrap()
        .finally(() => setDocumentUploadsLoading(false));
      dispatch(fetchDocumentContracts({ data: { user: currentUser.id } }))
        .unwrap()
        .finally(() => setDocumentContractsLoading(false));
    }
  }, [currentUser, dispatch, company]);

  const getTemplates = useCallback(() => {
    if (currentUser?.id) {
      setTemplatesLoading(true);
      NWClient.list('document-library')
        .then((res: LibraryItem[]) => {
          setTemplates(
            [...res]
              .filter((item: LibraryItem) => item.top_template && item.top_template_order)
              .sort((a, b) => Number(a.top_template_order) - Number(b.top_template_order))
          );
        })
        .finally(() => setTemplatesLoading(false));
    }
  }, [currentUser]);

  useEffect(() => {
    getCompanyAndProfiles();
  }, [getCompanyAndProfiles]);

  useEffect(() => {
    if (fetchingCompanyError === 'Request failed with status code 401') {
      signOut({ redirectUrl: ROUTES.LOGIN });
    }
  }, [fetchingCompanyError]);

  useEffect(() => {
    if (process.env.USERFLOW_ENABLED === 'TRUE' && company && currentUser.id) {
      userflow.init(process.env.USERFLOW_TOKEN);
      userflow.identify(currentUser.user_uuid, {
        name: currentUser.first_name + ' ' + currentUser.last_name,
        signed_up_at: currentUser.date_joined,
        email: currentUser.email,
        is_company_member: company.members.some(
          (member) => member.user_uuid === currentUser.user_uuid
        ),
        is_subscribed: typeof company.plan_settings.free === 'object',
      });
    }
  }, [company, currentUser.id]);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  useEffect(() => {
    getTemplates();
  }, [getTemplates]);

  const BlankDocumentModal: React.FC<{ isBlankModalOpen: boolean; onClose: () => void }> = ({
    isBlankModalOpen,
    onClose,
  }) => {
    const [documentName, setDocumentName] = useState('');
    const [documentCategory, setDocumentCategory] = useState('business');
    const handleBlankDocumentCreation = (newname: string, category: string) => {
      const blankDocData = {
        data: {
          user: currentUser.id,
          name: newname,
          html: '<p>New document</p>',
          category: category,
        },
      };

      try {
        dispatch(addDocumentContract(blankDocData))
          .unwrap()
          .then((res: AIDocumentContractModel) => {
            navigate(`${ROUTES.DOCUMENT_VAULT}/${res.id}?initialTab=0&contractGenerated=true`);
            setIsBlankModalOpen(false);
          })
          .catch((error) => {
            // Handle any errors
            console.error('Failed to create document:', error);
          });
        // setIsBlankModalOpen(false); // Close the modal after the operation finishes
      } catch (error) {
        console.error('Error copying library item:', error);
      }
    };
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const newDocumentName = formData.get('newDocumentName') as string;
      const category = formData.get('category') as string;
      handleBlankDocumentCreation(newDocumentName, category);
    };
    return (
      <Modal
        className='action-modal'
        open={isBlankModalOpen}
        sx={{
          position: 'absolute',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          width: '50%',
          top: '0',
          left: '25%',
          margin: 0,
          padding: 0,
        }}
        onClose={onClose}
      >
        <Grid container>
          <Grid item xs={12} sm={11} md={11} lg={11}>
            <Card>
              <CardContent sx={{ paddingBottom: '16px !important' }}>
                <Typography variant={'h6'} gutterBottom>
                  Create a blank document
                </Typography>

                <Typography>Please provide a name for the document.</Typography>
                <form onSubmit={handleSubmit}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      marginTop: '0.5rem',
                    }}
                  >
                    <TextField
                      name='newDocumentName'
                      placeholder='New document name'
                      label='Document name'
                      variant='outlined'
                      fullWidth
                      required={true}
                      value={documentName}
                      onChange={(e) => setDocumentName(e.target.value)}
                    />
                    <FormControl fullWidth>
                      <InputLabel required={true} id='demo-simple-select-label'>
                        Category
                      </InputLabel>
                      <Select
                        labelId='demo-simple-select-label'
                        id='demo-simple-select'
                        name='category'
                        label='Category'
                        required={true}
                        value={documentCategory}
                        onChange={(e) => setDocumentCategory(e.target.value)}
                      >
                        <MenuItem value='employment'>Employment</MenuItem>
                        <MenuItem value='customers'>Customers</MenuItem>
                        <MenuItem value='suppliers'>Suppliers</MenuItem>
                        <MenuItem value='business'>Business</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Grid container justifyContent='flex-end' gap={'8px'} mt={2}>
                    <Button
                      variant='contained'
                      onClick={() => setIsBlankModalOpen(false)}
                      className={'btn-default btn-action-basic btn-cancel'}
                    >
                      Go back
                    </Button>

                    <Button
                      variant='contained'
                      type='submit'
                      className='btn-default btn-action-basic'
                      disabled={!documentCategory || !documentName}
                    >
                      Confirm
                    </Button>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Modal>
    );
  };

  return (
    <>
      <div className='d-md-flex home-page-wrapper'>
        <div className='flex-md-grow-1 d-md-flex flex-md-column'>
          <div className='d-md-flex flex-md-column'>
            <div className='flex-md-grow-1'>
              <Box className='box-with-header flex-grow-1'>
                <Box className='header-action-row d-flex justify-content-between'>
                  <Typography variant='h2'>{t('headers.documents')}</Typography>
                </Box>
                <Box
                  gap={'1.1%'}
                  className='d-flex align-items-start flex-wrap buttons-container action-controls-container'
                >
                  <FormControl>
                    <InputLabel id='createNewDocumentLabel'>
                      {t('labels.createDocument')}
                    </InputLabel>
                    <Select
                      labelId='createNewDocumentLabel'
                      value={createNewDocument}
                      IconComponent={ExpandMoreIcon}
                      MenuProps={{
                        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                      onChange={(event: SelectChangeEvent) =>
                        setCreateNewDocument(event.target.value)
                      }
                      input={<OutlinedInput />}
                    >
                      <MenuItem value='selectAnOption'>{t('menuItems.selectAnOption')}</MenuItem>
                      <MenuItem value='createFromBusinessKitzLibrary'>
                        {t('menuItems.createFromBusinessKitzLibrary')}
                      </MenuItem>
                      <MenuItem value='createFromOwnLibrary'>
                        {t('menuItems.createFromOwnLibrary')}
                      </MenuItem>
                      {company && (
                        <MenuItem value='createNewBlankDocument'>
                          {t('menuItems.createNewBlankDocument')}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <ListItemButton
                    buttonText={t('buttons.store')}
                    onClick={() => navigate(ROUTES.DOCUMENT_VAULT)}
                  />
                  <ListItemButton
                    buttonText={t('buttons.sign')}
                    onClick={() => navigate(ROUTES.DOCUMENTS)}
                  />
                </Box>
              </Box>
            </div>
            <div className='flex-md-grow-1'>
              <Box className='box-with-header flex-grow-1'>
                <Box className='header-action-row d-flex justify-content-between'>
                  <Typography variant='h2'>{t('headers.profiles')}</Typography>
                </Box>
                <Box
                  gap={'1.1%'}
                  className='d-flex align-items-start flex-wrap four-items buttons-container'
                >
                  <ListItemButton
                    buttonText={t('buttons.business')}
                    onClick={() => navigate(ROUTES.BUSINESS_PROFILE)}
                  />
                  <ListItemButton
                    buttonText={t('buttons.employee')}
                    onClick={() => navigate(ROUTES.EMPLOYEE_PROFILES)}
                  />
                  <ListItemButton
                    buttonText={t('buttons.customer')}
                    onClick={() => navigate(ROUTES.CUSTOMER_PROFILES)}
                  />
                  <ListItemButton
                    buttonText={t('buttons.supplier')}
                    onClick={() => navigate(ROUTES.SUPPLIER_PROFILES)}
                  />
                </Box>
              </Box>
            </div>
          </div>
          <div className='d-flex flex-grow-1 flex-column' style={{ height: `calc(50% - 16px)` }}>
            <Box className='position-relative box-with-header flex-grow-1'>
              <Box className='header-action-row d-flex justify-content-between'>
                <Typography variant='h2'>{t('headers.toDo')}</Typography>
                {company && (
                  <Link to={null}>
                    {!isSkipped && (
                      <Button
                        variant='contained'
                        className='btn-action-basic btn-small'
                        onClick={handleSkip}
                      >
                        Skip
                      </Button>
                    )}
                    {isSkipped && (
                      <span
                        role='button'
                        tabIndex={-1}
                        onKeyUp={() => {
                          return;
                        }}
                        className='add-icon-wrapper d-flex justify-content-center align-items-center'
                        onClick={handleOpen}
                      >
                        <AddIcon />
                      </span>
                    )}
                  </Link>
                )}
              </Box>
              {company ? (
                <>
                  {!isSkipped && (
                    <List className='tasks-list pt-0 pb-0'>
                      <ListItem>
                        <TaskItem taskText={'Upload and store a document'} />
                      </ListItem>
                      <ListItem>
                        <TaskItem taskText={'Create new document'} taskCompleted />
                      </ListItem>
                      <ListItem>
                        <TaskItem taskText={'Add employee'} />
                      </ListItem>
                      <ListItem>
                        <TaskItem
                          taskText={'Create a template from existing AI components'}
                          taskCompleted
                        />
                      </ListItem>
                      <ListItem>
                        <TaskItem taskText={'Create new document'} taskCompleted />
                      </ListItem>
                      <ListItem>
                        <TaskItem taskText={'Add a supplier or customer'} />
                      </ListItem>
                      <ListItem>
                        <TaskItem taskText={'Document summary with AI'} taskCompleted />
                      </ListItem>
                      <ListItem>
                        <TaskItem taskText={'Start subscription'} />
                      </ListItem>
                    </List>
                  )}
                  {isSkipped && todos.length === 0 && !todosLoading && (
                    <Box className='d-flex flex-column flex-grow-1 justify-content-center align-items-center empty-state-message-container '>
                      <div className='empty-state-message'>{t('emptyStateMessages.todos')}</div>
                    </Box>
                  )}
                  {isSkipped && todos.length > 0 && (
                    <List className='tasks-list pt-0 pb-0'>
                      {sortTodos(todos).map((todo) => (
                        <ListItem key={todo.id}>
                          <TaskItem
                            taskText={todo.message}
                            date={new Date(todo.due_at)}
                            status={getTodoStatus(todo)}
                            iconLeft
                            onAction={(action) => handleAction(action, todo)}
                            actions
                            onStart={() => handleStart(todo.id)}
                            onComplete={() => handleComplete(todo.id)}
                            onContinue={() => handleContinue(todo.id)}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                  {(todosLoading || todosPending === 'loading') && (
                    <ProgressOverlay className='position-absolute' />
                  )}
                </>
              ) : (
                <Box className='d-flex flex-column flex-grow-1 justify-content-center align-items-center empty-state-message-container '>
                  <CompanyCreateForm />
                </Box>
              )}
            </Box>
          </div>
        </div>
        <div className='flex-md-shrink-0 d-md-flex flex-md-column'>
          <div className='flex-grow-1'>
            <ListItemBlock
              orderedList={true}
              withFixedMaximumHeight={true}
              headerText={t('headers.topDocumentTemplates')}
              listArray={templatesArray}
              withMenu={false}
              emptyStateMessage={t('messages.topTemplatesEmptyMessage')}
              loading={templatesLoading}
            />
          </div>
          <div>
            <Box className='promo-block' height='100%' sx={{ backgroundColor: '#ffb' }}>
              <Typography variant='h2' component='h2'>
                {t('headers.legalKitz')}
              </Typography>
              <p>{t('messages.legalKitzPartnerMessage')}</p>
              <p>{t('messages.ctaMessageLegalKitz')}</p>
              <Box display='flex' gap={'1.1%'}>
                <Button
                  variant='contained'
                  onClick={() => toggleShowBookingModal()}
                  /*onClick={() => window.open(COMMON_LINKS.legal_kitz_book_call_link, '_blank')}*/
                  className='btn-default btn-action-basic'
                >
                  {t('buttons.book')}
                </Button>
              </Box>
            </Box>
          </div>
        </div>
      </div>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '8px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '600px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            padding: '16px',
          }}
        >
          {open && <TodoForm existingTodo={selectedTodo} onClose={() => setOpen(false)} />}
        </Box>
      </Modal>
      <BlankDocumentModal
        isBlankModalOpen={isBlankModalOpen}
        onClose={() => {
          setIsBlankModalOpen(false);
          setCreateNewDocument('selectAnOption');
        }}
      />
      {loading || (pendingDownload && <ProgressOverlay />)}
      {isBookingModalOpen && (
        <BookACallModal
          company={company}
          user={currentUser}
          open={isBookingModalOpen}
          onClose={() => toggleShowBookingModal()}
        />
      )}
    </>
  );
};

export default HomePageBlock;
